import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, Divider, Drawer, IconButton, Stack, Tooltip, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import { getIpamAddressHistory } from '../../../../services/ipamApi';
import { PortalLoading, PortalEmptyState } from '../../../../components/portal/PortalStates';
import {
  CK, FONT_BODY, FONT_MONO, FONT_TITLE, cidrCellSx, insetPanelSx, statusChipSx,
} from '../../portalStyles';
import { formatStatusLabel } from '../../../../utils/statusLabel';

// IpamHistoryDrawer — per-CIDR address history (DD-35).
//
// A DRAWER, NEVER A ROUTE. GetIpamAddressHistory is a per-(Cidr, IpamScopeId)
// lookup with NO bulk feed: there is no "list all history" call to page. A route
// for it would therefore render an empty search box on arrival with nothing to
// list, and would have to make the user retype a CIDR they were already looking
// at. It opens from a CIDR cell on Address Space, Pool Detail and Allocations
// instead, so the CIDR and the scope are already in hand.
//
// IT IS THE ONLY LIVE-AWS GET IN THE CAPABILITY. Everything else on these screens
// is a read of Prism's own cache. Three consequences drive this file:
//
//   1. It can be THROTTLED, which nothing else here can. A throttle is drawn as
//      its own amber state with its own words — never as the generic red failure
//      card, because "something went wrong" invites the user to hammer the retry
//      button against the exact call that is rate-limited. The retry is
//      additionally held on a short cooldown for the same reason.
//   2. It is NOT CACHED and cannot be. Its source is destroyed irreversibly by
//      DeleteIpam, so a cache would outlive the thing it mirrors and start lying
//      at exactly the moment the answer matters.
//   3. `cidr` and `scopeId` are BOTH REQUIRED — the backend answers 400 before it
//      dials AWS. This component refuses to call rather than spending a request
//      to be told so, and says which one is missing.
//
// PROP CONTRACT — three call sites depend on it, so it is deliberately small and
// obvious:
//
//   open       bool                     — controlled, as every portal drawer is
//   onClose    () => void
//   cidr       string                   — the CIDR whose history to look up
//   scopeId    string                   — the ipam-scope-… the lookup is made in
//   scopeLabel string   (optional)      — human name for the scope, caption only
//
// The two required data props are the two query parameters, in the same spelling
// the API client uses, so a call site cannot half-remember which is which.

const IPAM_COLOR = '#455A64';

// A THROTTLE IS RECOGNISED BY ITS MESSAGE, AND THAT IS NOT A LIMITATION OF
// portalFetch — it now attaches `err.status` and `err.body`
// (services/portalApi.js:110-113), and IpamPoolDetail.js:145 already relies on it.
// It is a limitation of the ROUTE: the handler answers 500 for a throttle, not
// 429, because the throttle predicate is unexported service-side and there is no
// status for it to map to (services/ipamApi.js documents this on
// getIpamAddressHistory). So the status carries no information here and the text
// carries all of it. The pattern is deliberately wide: every spelling AWS and the
// SDK use for the same condition maps to the same state, because the cost of
// missing one is showing a red "something went wrong" for a condition whose only
// correct advice is "wait".
const THROTTLE_PATTERN =
  /throttl|rate ?exceed|too many requests|requestlimitexceeded|slow ?down|\b429\b/i;

// Seconds the retry button stays disabled after a throttle. A throttled call
// answered instantly, so without this the button is live again before the reader
// has finished the sentence explaining why it failed — which is how a rate limit
// becomes a retry storm.
const THROTTLE_COOLDOWN_SEC = 10;

const THROTTLE_TEXT = 'AWS is throttling this lookup, try again in a moment.';

// THE READER IS INTOLERANT, FOR THE REASON THE THREE INVENTORY PAGES ARE.
//
// This reader used to try four envelope shapes and fall through to `[]`. That was
// right while the DTO was being written in a parallel lane and nothing pinned the
// wrapper; it is wrong now, because a reader that accepts four shapes cannot tell
// a renamed key from a normal response — and the failure it hides is this drawer
// rendering "AWS has no record of {cidr} ever being associated with a resource in
// this scope", which is a claim about AWS manufactured out of an envelope Prism
// did not recognise. IpamAddressSpace, IpamAllocations and IpamPublicIPs all
// replaced this exact shape with a throwing `inventoryPage()`.
//
// The contract is now fixed and one-shaped: the handler calls h.ok(w, records)
// with a []IpamAddressHistoryRecord the service guarantees non-nil, so `data` is
// a BARE ARRAY and never null. Anything else is a defect, and it surfaces as the
// drawer's error state rather than as an empty history.
const HISTORY_ENVELOPE_ERROR = 'Prism could not read this CIDR\u2019s address history: AWS\u2019s '
  + 'reply came back in a shape this drawer does not recognise. This is a defect, not an empty '
  + 'history — the drawer is deliberately not being drawn as "AWS has no record of this CIDR".';

const historyRecords = (data) => {
  if (!Array.isArray(data)) throw new Error(HISTORY_ENVELOPE_ERROR);
  return data;
};

// AWS's own struct is Go-cased (`ResourceCidr`); every Prism DTO in this portal
// is lowerCamel. Accept either per field rather than betting the drawer on which
// way the marshaller goes.
const field = (row, name) => {
  if (!row) return undefined;
  if (row[name] !== undefined && row[name] !== null) return row[name];
  const upper = name.charAt(0).toUpperCase() + name.slice(1);
  return row[upper];
};

const formatTime = (iso) => {
  if (!iso) return null;
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return null;
  return t.toLocaleString();
};

// Compliance / overlap on a history record carry AWS's vocabulary.
//
// THE LABELS AND INTENTS ARE IpamAddressSpace.js's, DELIBERATELY. This drawer
// opens FROM a row on that page, so a value spelled "No overlap" in the table and
// "Nonoverlapping" in the drawer that the table opened is the same fact rendered
// as two, and the reader has to work out they are one. Same for
// "Noncompliant". These maps mirror COMPLIANCE_META / OVERLAP_META there; keep
// them in step.
//
// AN UNRECOGNISED VALUE IS AMBER, NOT GREY, AND THAT IS THE WHOLE POINT.
// It used to fall back to `neutral`, which is the intent worn here by `ignored` —
// a DECIDED, benign outcome that means IPAM was asked to make no claim. Drawing a
// token nobody has mapped in that same grey makes an anomaly indistinguishable
// from a state Prism understands and considers fine, which is laundering
// (ipamState.js:36-52). So the day AWS adds a compliance value, the chip says the
// raw token in amber and admits Prism has no description for it — exactly as
// IpamAddressSpace.js:210-215 already does on the row this drawer was opened from.
const COMPLIANCE_META = {
  compliant: {
    intent: 'success',
    label: 'Compliant',
    hint: 'This CIDR sat inside space provisioned to an IPAM pool and matched that pool’s allocation rules.',
  },
  noncompliant: {
    intent: 'error',
    label: 'Noncompliant',
    hint: 'This CIDR was allocated from an IPAM pool but no longer matched its rules.',
  },
  unmanaged: {
    intent: 'warning',
    label: 'Unmanaged',
    hint: 'No IPAM pool governed this CIDR — the resource was given its addresses outside IPAM.',
  },
  ignored: {
    intent: 'neutral',
    label: 'Ignored',
    hint: 'This resource was excluded from IPAM monitoring, so IPAM makes no claim about it either way.',
  },
};

const OVERLAP_META = {
  nonoverlapping: {
    intent: 'success',
    label: 'No overlap',
    hint: 'No other CIDR in this scope collided with this one while it was held.',
  },
  overlapping: {
    intent: 'error',
    label: 'Overlapping',
    hint: 'Another CIDR in this scope collided with this one — often one in a different account, holding addresses that cannot both be routed.',
  },
  ignored: {
    intent: 'neutral',
    label: 'Ignored',
    hint: 'Excluded from overlap monitoring.',
  },
};

const UNMAPPED_HINT = (raw) =>
  `Prism has no description for the value “${raw}”. It is shown as reported rather than folded `
  + 'into a state it may not be.';

// RecordPostureChip draws one posture value, or nothing at all when the record
// carries none — a blank is not a pass and must not become a chip.
function RecordPostureChip({ value, meta }) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const m = meta[raw.toLowerCase()];
  if (!m) {
    return (
      <Tooltip title={UNMAPPED_HINT(raw)}>
        <Chip size="small" label={formatStatusLabel(raw)} sx={statusChipSx('warning')} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={m.hint}>
      <Chip size="small" label={m.label} sx={statusChipSx(m.intent)} />
    </Tooltip>
  );
}

function MetaLine({ label, value, mono }) {
  if (!value) return null;
  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', minWidth: 0 }}>
      <Typography
        sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontFamily: mono ? FONT_MONO : FONT_BODY,
          fontSize: '0.8125rem',
          color: CK.navy,
          minWidth: 0,
          wordBreak: 'break-all',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function HistoryRecord({ record }) {
  const start = formatTime(field(record, 'sampledStartTime'));
  const end = formatTime(field(record, 'sampledEndTime'));
  const compliance = field(record, 'resourceComplianceStatus');
  const overlap = field(record, 'resourceOverlapStatus');

  return (
    <Box sx={{ ...insetPanelSx, mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 1,
          flexWrap: 'wrap',
          mb: 1,
        }}
      >
        <Typography
          sx={{ fontFamily: FONT_MONO, fontSize: '0.875rem', color: CK.navy, wordBreak: 'break-all' }}
        >
          {field(record, 'resourceId') || field(record, 'resourceName') || 'Unnamed resource'}
        </Typography>
        {/* An absent end time means the association is STILL LIVE. Rendering it as
            a blank would make the current holder of the address look like one more
            expired row, which is the single most important row in the list. */}
        <Chip
          size="small"
          label={end ? 'Released' : 'Current'}
          sx={statusChipSx(end ? 'neutral' : 'info')}
        />
      </Box>

      <Stack spacing={0.5} sx={{ mb: 1 }}>
        <MetaLine label="Type" value={formatStatusLabel(field(record, 'resourceType'))} />
        <MetaLine label="Name" value={field(record, 'resourceName')} />
        <MetaLine label="Account" value={field(record, 'resourceOwnerId')} mono />
        <MetaLine label="Region" value={field(record, 'resourceRegion')} />
        <MetaLine label="VPC" value={field(record, 'vpcId')} mono />
        <MetaLine label="CIDR" value={field(record, 'resourceCidr')} mono />
      </Stack>

      {(compliance || overlap) && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
          <RecordPostureChip value={compliance} meta={COMPLIANCE_META} />
          <RecordPostureChip value={overlap} meta={OVERLAP_META} />
        </Stack>
      )}

      {/* AWS words this as SAMPLED, and so does this drawer. Discovery is a
          periodic sweep, so the real change happened at or BEFORE the stamp — a
          bare "from/to" would read as an exact audit trail it is not. */}
      <Tooltip title="IPAM picks changes up in periodic snapshots, so the real start and end happened at or before these sampled times.">
        <Typography
          sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, cursor: 'help' }}
        >
          Sampled {start || 'unknown'} → {end || 'still held'}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function IpamHistoryDrawer({ open, onClose, cidr, scopeId, scopeLabel }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [throttled, setThrottled] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // The same monotonic guard the filtered tables carry, for the same reason: the
  // drawer can be closed on one CIDR and reopened on another faster than a live
  // AWS call returns, and the slow first response would otherwise land in a drawer
  // that is now showing a different address — history attributed to the wrong CIDR
  // is worse than no history.
  const reqSeq = useRef(0);

  const missing = useMemo(() => {
    const gaps = [];
    if (!cidr) gaps.push('a CIDR');
    if (!scopeId) gaps.push('an IPAM scope');
    return gaps;
  }, [cidr, scopeId]);

  const load = useCallback(async () => {
    if (!cidr || !scopeId) return;
    const myReq = ++reqSeq.current;
    setLoading(true);
    setError(null);
    setThrottled(false);
    try {
      const res = await getIpamAddressHistory({ cidr, scopeId });
      if (myReq !== reqSeq.current) return;
      setRecords(historyRecords(res?.data));
    } catch (e) {
      if (myReq !== reqSeq.current) return;
      setRecords([]);
      if (THROTTLE_PATTERN.test(e?.message || '')) {
        setThrottled(true);
        setCooldown(THROTTLE_COOLDOWN_SEC);
      } else {
        setError(e?.message || 'Could not read address history.');
      }
    } finally {
      if (myReq === reqSeq.current) setLoading(false);
    }
  }, [cidr, scopeId]);

  // Keyed on the two PRIMITIVES, never on an object: a caller that rebuilds a
  // props object every render would otherwise re-issue the live AWS call on every
  // parent render — on the one call in this capability that AWS rate-limits.
  useEffect(() => {
    if (!open) return;
    setRecords([]);
    load();
  }, [open, cidr, scopeId, load]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const ordered = useMemo(() => {
    const ts = (r) => {
      const v = new Date(field(r, 'sampledStartTime') || 0).getTime();
      return Number.isNaN(v) ? 0 : v;
    };
    // Newest first: the current holder of the address is the row read first.
    return [...records].sort((a, b) => ts(b) - ts(a));
  }, [records]);

  const retryDisabled = loading || cooldown > 0;

  return (
    <Drawer
      anchor="right"
      open={!!open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 520 },
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: CK.surface,
        },
      }}
    >
      <Box sx={{ p: 2.5, backgroundColor: CK.pageBg, borderBottom: `1px solid ${CK.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontFamily: FONT_TITLE, fontWeight: 600, fontSize: '1.15rem', color: CK.navy }}
            >
              Address history
            </Typography>
            <Typography sx={{ ...cidrCellSx, border: 'none', fontSize: '0.9rem', mt: 0.5 }}>
              {cidr || '—'}
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted, mt: 0.25 }}>
              {scopeLabel || scopeId || 'no scope'}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Close address history">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ p: 2.5, flex: 1, overflowY: 'auto' }}>
        {/* Said BEFORE the results, not after: this is the only reading on these
            screens that is not a cache, and the only one whose source disappears
            for good when the IPAM is deleted. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{ mb: 2, color: CK.muted }}
        >
          <CloudSyncOutlinedIcon sx={{ fontSize: 18, mt: '2px', flexShrink: 0 }} />
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: '0.75rem', color: CK.muted }}>
            Read live from AWS each time this opens — nothing here is cached. AWS destroys
            this history irreversibly when the IPAM is deleted, so it cannot be exported or
            recovered afterwards.
          </Typography>
        </Stack>

        {missing.length > 0 ? (
          <Alert severity="info" sx={{ fontFamily: FONT_BODY }}>
            <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
              Nothing to look up
            </AlertTitle>
            This lookup needs {missing.join(' and ')}. AWS has no way to search address
            history without both, so no request was made.
          </Alert>
        ) : (
          <>
            {throttled && (
              // NOT the red error card, and not the word "failed". A throttle is a
              // rate limit on a live call: the only correct next action is to wait,
              // and a generic failure would push the reader to do the opposite.
              <Alert severity="warning" sx={{ fontFamily: FONT_BODY, mb: 2 }}>
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                  {THROTTLE_TEXT}
                </AlertTitle>
                Nothing is broken and nothing was lost. This is the only read in VPC IPAM
                that goes straight to AWS, so it is the only one AWS can rate-limit.
              </Alert>
            )}

            {error && !throttled && (
              <Alert severity="error" sx={{ fontFamily: FONT_BODY, mb: 2 }}>
                <AlertTitle sx={{ fontFamily: FONT_BODY, fontWeight: 700 }}>
                  Could not read address history
                </AlertTitle>
                {error}
              </Alert>
            )}

            {loading && ordered.length === 0 ? (
              <PortalLoading label="Asking AWS for this CIDR's history…" color={IPAM_COLOR} />
            ) : (
              <>
                {ordered.length > 0 && (
                  <>
                    <Typography
                      sx={{ fontFamily: FONT_BODY, fontSize: '0.8125rem', color: CK.body, mb: 1.5 }}
                    >
                      {ordered.length} {ordered.length === 1 ? 'record' : 'records'}, newest first.
                    </Typography>
                    {ordered.map((r, i) => (
                      <HistoryRecord
                        key={`${field(r, 'resourceId') || 'r'}:${field(r, 'sampledStartTime') || i}`}
                        record={r}
                      />
                    ))}
                  </>
                )}

                {!loading && !error && !throttled && ordered.length === 0 && (
                  <PortalEmptyState
                    icon={<HistoryIcon sx={{ fontSize: 28 }} />}
                    title="No history for this CIDR in this scope"
                    description={
                      `AWS has no record of ${cidr || 'this CIDR'} ever being associated with a ` +
                      'resource in this scope. History only starts when IPAM first discovers a ' +
                      'resource holding the address, so a prefix that has never been used, or one ' +
                      'used before this IPAM existed, has nothing to show.'
                    }
                    color={IPAM_COLOR}
                    py={3}
                  />
                )}
              </>
            )}
          </>
        )}
      </Box>

      {missing.length === 0 && (
        <>
          <Divider />
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={load}
              disabled={retryDisabled}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              {cooldown > 0 ? `Try again in ${cooldown}s` : 'Refresh'}
            </Button>
            <Button
              size="small"
              onClick={onClose}
              sx={{ fontFamily: FONT_BODY, textTransform: 'none' }}
            >
              Close
            </Button>
          </Box>
        </>
      )}
    </Drawer>
  );
}
